import { notify } from '@kyvg/vue3-notification'
import html2pdf from 'html2pdf.js'
import { log } from '@/libs/Logger.js'
import { sanitizeNumber } from '@/components/controllers/FormatController'
import axios from 'axios'
import store from '@/store/index.js'

/**
 * Store values into localStorage.
 *
 * @param {String} varName
 * @param {*} value
 */
const setLocalVar = (varName, value) => {
    if (!localStorage.getItem(varName)) {
        localStorage.setItem(varName, JSON.stringify({}))
    }
    localStorage.setItem(varName, JSON.stringify(value))
}

/**
 * Return values stored into localStorage.
 *
 * @param {String} varName
 */
const getLocalVar = function (varName) {
    return JSON.parse(localStorage.getItem(varName))
}

/**
 * Deletes values stored into localStorage.
 *
 * @param {String} varName
 */
const clearLocalVar = function (varName) {
    localStorage.removeItem(varName)
}

// Convert file to base64 string
const fileToBase64 = (filename, filepath) => {
    return new Promise(resolve => {
        const file = new File([filename], filepath)
        const reader = new FileReader()
        // Read file content on file loaded event
        reader.onload = function (event) {
            resolve(event.target.result)
        }

        // Convert data to base64
        reader.readAsDataURL(file)
    })
}

const showErrorNotification = function (message) {
    notify({
        type: 'error',
        title: message.title,
        text: message.text
    })
}

const showErrorNotificationFixed = function (message) {
    notify({
        type: 'error',
        title: message.title,
        text: message.text,
        duration: -1
    })
}

const showSuccessNotification = function (message) {
    notify({
        type: 'success',
        title: message.title,
        text: message.text
    })
}

const showAlertNotification = function (message) {
    notify({
        type: 'warn',
        title: message.title,
        text: message.text
    })
}

const pdfExport = async function ({ id, options = null, fileName, css = null }) {
    try {
        const newDocument = document.implementation.createHTMLDocument(fileName)
        const content = document.getElementById(id)
        const clonedContent = content.cloneNode(true)

        if (css) {
            css.forEach(curCss => {
                const { elClass, value } = curCss
                let elementListByClass = Object.values(clonedContent.getElementsByClassName(elClass))
                if (elementListByClass.length) {
                    elementListByClass.forEach(el => {
                        el.setAttribute('style', value)
                    })
                } else {
                    elementListByClass = Object.values(clonedContent.getElementsByTagName(elClass))
                    elementListByClass.forEach(el => {
                        el.setAttribute('style', value)
                    })
                }
            })
        }

        newDocument.getElementsByTagName('body')[0].append(clonedContent)
        const pdfContent = newDocument.getElementById(id)

        const newWindow = window.open()
        newWindow.document.write(newDocument.documentElement.outerHTML)
        newWindow.document.close()

        const pdfOptions = options || {
            margin: 10,
            filename: fileName + '.pdf',
            image: { type: 'jpeg', quality: 0.98 },
            html2canvas: { scale: 2 },
            jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
        }

        html2pdf().from(pdfContent).set(pdfOptions).save()
        return true
    } catch (error) {
        log({ type: 'error', message: `Erro em pdfExport:\n${error.message}` })
        return error
    }
}

const findCep = async (cep) => {
    cep = sanitizeNumber(cep)

    if (cep.length === 8) {
        try {
            const response = await axios.get(`https://viacep.com.br/ws/${cep}/json/`)
            const data = response.data
            if (data.erro) throw new Error('Cep inválido')
            return data
        } catch {
            return null
        }
    }

    return null
}

const getFlagName = function (flag) {
    flag = flag.toLocaleLowerCase()

    const flagNames = [
        'alelo',
        'alipay',
        'american express',
        'amex',
        'aura',
        'diners',
        'discover',
        'elo',
        'hiper',
        'hipercard',
        'jcb',
        'maestro',
        'mastercard',
        'mir',
        'paypal',
        'sodexo',
        'unionpay',
        'visa'
    ]

    for (let i = 0; i < flagNames.length; i++) {
        const flagName = flagNames[i]
        if (flag.includes(flagName)) return flagName
    }

    return ''
}

const setAppColors = () => {
    const coresJson = store.getters.retailGeneralParams.CORESJSON || null
    const darkTheme = store.getters.retailGeneralParams.TEMAESCURO || null
    if (coresJson) {
        try {
            const colors = JSON.parse(coresJson)
            const root = document.querySelector(':root')

            if (darkTheme) {
                root.style.setProperty('--primaryColor', '#' + colors.DARK.CORPRIMARIA)
                root.style.setProperty('--textColor', '#FFFFFF')
                root.style.setProperty('--backgroundColor', '#000000')
                root.style.setProperty('--buttonBackground', '#FFFFFF')
                root.style.setProperty('--backgroundColorOverlay', 'rgba(200, 200, 200, 0.20)')
                root.style.setProperty('--buttonText', '#000000')
                root.style.setProperty('--iconColor', '#FFFFFF')
                root.style.setProperty('--cardBackground', '#' + colors.DARK.CORFUNDOCARD)
                root.style.setProperty('--cardBorder', '#' + colors.DARK.CORBORDACARD)
            } else {
                root.style.setProperty('--primaryColor', '#' + colors.DEFAULT.CORPRIMARIA)
                root.style.setProperty('--textColor', '#000000')
                root.style.setProperty('--backgroundColor', '#FFFFFF')
                root.style.setProperty('--backgroundColorOverlay', 'rgba(0, 0, 0, 0.20)')
                root.style.setProperty('--buttonBackground', '#' + colors.DEFAULT.CORPRIMARIA)
                root.style.setProperty('--buttonText', '#FFFFFF')
                root.style.setProperty('--iconColor', '#' + colors.DEFAULT.CORPRIMARIA)
                root.style.setProperty('--cardBackground', '#' + colors.DEFAULT.CORFUNDOCARD)
                root.style.setProperty('--cardBorder', '#' + colors.DEFAULT.CORBORDACARD)
            }
        } catch (error) {
            setDefaultColors()
            log({ type: 'error', message: `Erro ao tentar setar as cores customizadas:\n${error.message}` })
        }
    } else {
        setDefaultColors()
    }
}

const setDefaultColors = () => {
    const root = document.querySelector(':root')
    root.style.setProperty('--primaryColor', '#040486')
    root.style.setProperty('--textColor', '#000000')
    root.style.setProperty('--backgroundColor', '#FFFFFF')
    root.style.setProperty('--buttonBackground', '#040486')
    root.style.setProperty('--backgroundColorOverlay', 'rgba(0, 0, 0, 0.20)')
    root.style.setProperty('--buttonText', '#FFFFFF')
    root.style.setProperty('--iconColor', '#040486')
    root.style.setProperty('--cardBackground', '#F2F6FC')
    root.style.setProperty('--cardBorder', '#E8E8E8')
}

const getWppHelpNumber = () => {
    const NUMEROAJUDA = store.getters.retailGeneralParams?.NUMEROAJUDA ? store.getters.retailGeneralParams.NUMEROAJUDA : ''
    return NUMEROAJUDA
}

const callWppHelp = () => {
    const NUMEROAJUDA = getWppHelpNumber()
    window.open(`https://api.whatsapp.com/send?phone=${NUMEROAJUDA}&text=Ol%C3%A1%2C%20preciso%20de%20ajuda%20com%20o%20NoCash.`, '_blank')
    return false
}

const hasWppParam = () => {
    return getWppHelpNumber().length > 0
}

export {
    setLocalVar,
    getLocalVar,
    clearLocalVar,
    fileToBase64,
    showErrorNotification,
    showErrorNotificationFixed,
    showSuccessNotification,
    pdfExport,
    showAlertNotification,
    findCep,
    getFlagName,
    setAppColors,
    callWppHelp,
    hasWppParam
}
